import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import supabase from '../supabase';
import AdminNav from '../components/AdminNav';

export default function SurveyDetail() {
    const navigate = useNavigate();
    const { id } = useParams();
    const [survey, setSurvey] = useState(null);
    const [questions, setQuestions] = useState([]);
    const [answers, setAnswers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [username, setUsername] = useState('');
    const [role, setRole] = useState('');
    const [view, setView] = useState('cards');
    const [copied, setCopied] = useState(false);

    useEffect(() => {
        const checkUser = async () => {
            const { data } = await supabase.auth.getSession();
            if (!data.session) {
                navigate('/login');
            }
        };
        checkUser();
    }, [navigate]);

    useEffect(() => {
        const fetchUser = async () => {
            const { data } = await supabase.auth.getSession();
            if (!data.session) return;
            const { data: userData } = await supabase
                .from('users')
                .select('username, role')
                .eq('email', data.session.user.email)
                .single();
            if (userData) { setUsername(userData.username); setRole(userData.role); }
        };
        fetchUser();
    }, []);

    const handleLogout = async () => {
        await supabase.auth.signOut();
        navigate('/login');
    };

    useEffect(() => {
        const fetchData = async () => {
            const { data: s } = await supabase.from('surveys').select('*').eq('id', id).single();
            if (s) setSurvey(s);

            const { data: q } = await supabase.from('survey_questions').select('*').eq('survey_id', id).order('position');
            if (q) setQuestions(q);

            const { data: a } = await supabase.from('survey_answers').select('*').eq('survey_id', id).order('submitted_at', { ascending: false });
            if (a) setAnswers(a);

            setLoading(false);
        };
        fetchData();
    }, [id]);

    const handleTogglePublish = async () => {
        const newPublished = !survey.published;
        await supabase.from('surveys').update({ published: newPublished }).eq('id', id);
        setSurvey({ ...survey, published: newPublished });
    };

    const handleDelete = async () => {
        if (!window.confirm('Supprimer ce survey et toutes ses réponses ?')) return;
        await supabase.from('survey_answers').delete().eq('survey_id', id);
        await supabase.from('survey_questions').delete().eq('survey_id', id);
        await supabase.from('surveys').delete().eq('id', id);
        navigate('/admin/surveys');
    };

    const handleCopyLink = () => {
        const url = `${window.location.origin}/s/${survey.slug}`;
        navigator.clipboard.writeText(url);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
    };

    const cellStyle = { padding: '10px 12px', fontSize: '12px', color: '#333', borderBottom: '0.5px solid #e0e0e0', verticalAlign: 'top' };
    const headStyle = { padding: '10px 12px', fontSize: '10px', fontWeight: '500', letterSpacing: '0.08em', textTransform: 'uppercase', color: '#aaa', borderBottom: '0.5px solid #e0e0e0', textAlign: 'left', whiteSpace: 'nowrap' };

    if (loading) return <div style={{ padding: '60px', textAlign: 'center', color: '#aaa', fontFamily: 'Helvetica Neue' }}>Chargement...</div>;

    return (
        <div style={{ display: 'flex', flexDirection: 'column', height: '100vh', fontFamily: 'Helvetica Neue, Helvetica, Arial, sans-serif' }}>

            {/* Topbar */}
            <AdminNav
                username={username}
                role={role}
                onLogout={handleLogout}
                activePath="/admin/surveys"
            />

            {/* Main */}
            <div style={{ flex: 1, padding: '24px 28px', overflowY: 'auto', background: '#f7f7f7' }}>

                {/* Header */}
                <div style={{ marginBottom: '20px' }}>
                    <p style={{ fontSize: '11px', color: '#888', cursor: 'pointer', marginBottom: '8px' }} onClick={() => navigate('/admin/surveys')}>← Back</p>

                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
                        <h1 style={{ fontWeight: '400', fontSize: '20px', color: '#111', margin: 0 }}>{survey.title}</h1>
                        <span style={{ fontSize: '10px', padding: '2px 8px', borderRadius: '10px', background: survey.published ? '#f0fdf4' : '#f5f5f5', color: survey.published ? '#22c55e' : '#aaa', border: `0.5px solid ${survey.published ? '#22c55e' : '#e0e0e0'}` }}>
                            {survey.published ? 'Publié' : 'Brouillon'}
                        </span>
                    </div>
                    <p style={{ fontSize: '11px', color: '#aaa', margin: '0 0 4px' }}>/s/{survey.slug} · {answers.length} réponse{answers.length !== 1 ? 's' : ''}</p>
                    {survey.description && <p style={{ fontSize: '13px', color: '#888', margin: '0 0 12px' }}>{survey.description}</p>}

                    {/* Actions */}
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', alignItems: 'center' }}>
                        <div style={{ display: 'flex', border: '0.5px solid #e0e0e0', borderRadius: '20px', overflow: 'hidden', background: 'white' }}>
                            {['cards', 'table'].map((v) => (
                                <button key={v} onClick={() => setView(v)} style={{ padding: '4px 12px', fontSize: '11px', border: 'none', background: view === v ? '#111' : 'white', color: view === v ? 'white' : '#888', cursor: 'pointer', fontFamily: 'Helvetica Neue, Helvetica, Arial, sans-serif' }}>
                                    {v === 'cards' ? '⊞ Cards' : '☰ Table'}
                                </button>
                            ))}
                        </div>
                        <button onClick={() => navigate(`/admin/surveys/${id}/edit`)} style={{ padding: '4px 12px', background: 'none', border: '0.5px solid #e0e0e0', borderRadius: '20px', fontSize: '11px', color: '#888', cursor: 'pointer' }}>✏️ Modifier</button>
                        <button onClick={handleCopyLink} style={{ padding: '4px 12px', background: 'none', border: '0.5px solid #e0e0e0', borderRadius: '20px', fontSize: '11px', color: '#888', cursor: 'pointer' }}>
                            {copied ? '✓ Copié !' : '🔗 Copier le lien'}
                        </button>
                        <button onClick={handleTogglePublish} style={{ padding: '4px 12px', background: survey.published ? 'white' : 'black', color: survey.published ? '#888' : 'white', border: survey.published ? '0.5px solid #e0e0e0' : 'none', borderRadius: '20px', fontSize: '11px', cursor: 'pointer' }}>
                            {survey.published ? 'Dépublier' : 'Publier →'}
                        </button>
                        <button onClick={handleDelete} style={{ padding: '4px 12px', background: 'none', border: '0.5px solid #ff4444', borderRadius: '20px', fontSize: '11px', color: '#ff4444', cursor: 'pointer' }}>Supprimer</button>
                    </div>
                </div>
                {answers.length === 0 && (
                    <div style={{ textAlign: 'center', padding: '60px', color: '#aaa', fontSize: '13px' }}>
                        Aucune réponse pour l'instant.{survey.published ? '' : ' Publie le survey pour commencer à collecter.'}
                    </div>
                )}

                {/* Cards view */}
                {view === 'cards' && answers.length > 0 && (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                        {answers.map((a) => (
                            <div key={a.id} style={{ background: 'white', border: '0.5px solid #e0e0e0', borderRadius: '10px', padding: '18px 20px' }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '14px' }}>
                                    <span style={{ fontSize: '11px', color: '#aaa' }}>{new Date(a.submitted_at).toLocaleDateString('fr-CA')}</span>
                                    {a.lang && <span style={{ fontSize: '10px', color: '#aaa', background: '#f5f5f5', borderRadius: '10px', padding: '2px 8px', textTransform: 'uppercase' }}>{a.lang}</span>}
                                </div>
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                                    {questions.map((q) => {
                                        const ans = a.answers?.[q.id];
                                        if (!ans) return null;
                                        return (
                                            <div key={q.id}>
                                                <p style={{ fontSize: '10px', fontWeight: '500', letterSpacing: '0.08em', textTransform: 'uppercase', color: '#aaa', margin: '0 0 3px' }}>{q.label}</p>
                                                {Array.isArray(ans) ? (
                                                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                                                        {ans.map((v) => <span key={v} style={{ fontSize: '11px', padding: '3px 10px', border: '0.5px solid #e0e0e0', borderRadius: '20px', color: '#555' }}>{v}</span>)}
                                                    </div>
                                                ) : (
                                                    <p style={{ fontSize: '13px', color: '#333', margin: 0 }}>{ans}</p>
                                                )}
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        ))}
                    </div>
                )}

                {/* Table view */}
                {view === 'table' && answers.length > 0 && (
                    <div style={{ background: 'white', border: '0.5px solid #e0e0e0', borderRadius: '10px', overflow: 'hidden' }}>
                        <div style={{ overflowX: 'auto' }}>
                            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px' }}>
                                <thead>
                                    <tr style={{ background: '#fafafa' }}>
                                        <th style={headStyle}>Date</th>
                                        {questions.map(q => <th key={q.id} style={headStyle}>{q.label}</th>)}
                                    </tr>
                                </thead>
                                <tbody>
                                    {answers.map((a) => (
                                        <tr key={a.id}>
                                            <td style={{ ...cellStyle, whiteSpace: 'nowrap', color: '#aaa' }}>{new Date(a.submitted_at).toLocaleDateString('fr-CA')}</td>
                                            {questions.map(q => {
                                                const ans = a.answers?.[q.id];
                                                return (
                                                    <td key={q.id} style={{ ...cellStyle, maxWidth: '200px' }}>
                                                        {Array.isArray(ans) ? ans.join(', ') : ans || '—'}
                                                    </td>
                                                );
                                            })}
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}